import { Button, Dialog, FormControl, FormLabel, Tooltip } from "@mui/material";
import {
  buyAllUntil10,
  calculateNewProduceTime,
  calculateNewProducerStrengthiningMultiplier,
  calculateSpeedShopCost,
  generateProducts,
  getCarbonMonoxideMultiplier,
  getCost,
  getCostsTillNextTimes10Upgrade,
  getProgress,
  getPsToMultiplyId,
  getTimes10Progress,
  innerGetCost,
  isMoleculeUnlocked,
} from "../lib/calculator";
import { prettifyNumber } from "../lib/prettifier";
import {
  BuySetting,
  Producer,
  neededPsForSb,
  speedFactor,
} from "../types/game";
import ProgressButton from "./basic/ProgressButton";
import React, { useContext, useEffect, useMemo } from "react";
import { SnackbarContext } from "../provider/SnackbarProvider";
import { GameContext } from "../provider/GameProvider";
import { getAchievementById, unlockAchievement } from "../types/achievements";
import Decimal from "break_infinity.js";
import executeProducerStrengthening, {
  executeBulkProducerStrengthening,
} from "../lib/resets";
import { loadObjectFromLocalStorage } from "../save/localSave";

export function BuyProducerButton({
  onClick,
  producer,
}: {
  onClick: () => void;
  producer: Producer;
}) {
  const { buySetting, game, achievementMultiplier } = useContext(GameContext);
  const cost = getCost(producer);
  let content = (
    <div>
      <div>Buy 1</div>
      <div>Cost: {prettifyNumber(cost)} P</div>
    </div>
  );

  if (buySetting === BuySetting.BuyUntilTen) {
    const costTill10 = getCostsTillNextTimes10Upgrade(producer);
    content = (
      <div>
        <div>Buy 10</div>
        <div>Cost: {prettifyNumber(costTill10)} P</div>
      </div>
    );
  }
  const progress = getProgress(cost, game.producers[0].value);
  const multiplier = useMemo(() => {
    return generateProducts(producer, achievementMultiplier, game).div(
      producer.value
    );
  }, [achievementMultiplier, game, producer]);

  return (
    <ProgressButton
      onClick={onClick}
      progress={progress}
      content={content}
      times10Progress={getTimes10Progress(producer.upgradeLevel)}
      multiplier={multiplier}
      isFirstProducer={producer.layerLevel === 1}
    />
  );
}

export function PsButton(props: { setPsDialogOpen: (open: boolean) => void }) {
  const { game, setGame, setTimeProgressArray } = useContext(GameContext);
  const hidePsDialog =
    loadObjectFromLocalStorage("hideProducerStrengtheningDialog") ?? false;

  const [bulk, setBulk] = React.useState(0);

  const unlockProducerName = useMemo(() => {
    if (game.producerStrengthening.upgradeLevel + 5 < game.producers.length) {
      return game.producers[game.producerStrengthening.upgradeLevel + 5].name;
    }
    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.producerStrengthening.upgradeLevel, game.producers.length, bulk]);

  const unlockProducerNameBulk = useMemo(() => {
    if (
      game.producerStrengthening.upgradeLevel + 5 + bulk <
      game.producers.length
    ) {
      return game.producers[game.producerStrengthening.upgradeLevel + 5 + bulk]
        .name;
    }
    return "";
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.producerStrengthening.upgradeLevel, game.producers.length, bulk]);

  const requiredUnits = useMemo(() => {
    if (!unlockProducerName) {
      return (
        (game.producerStrengthening.upgradeLevel + 6 - game.producers.length ||
          1) * 20
      );
    }
    return 20;
  }, [
    game.producerStrengthening.upgradeLevel,
    game.producers.length,
    unlockProducerName,
  ]);
  const requiredUnitsBulk = useMemo(() => {
    if (!unlockProducerNameBulk) {
      return (
        (game.producerStrengthening.upgradeLevel +
          6 +
          bulk -
          game.producers.length || 1) * 20
      );
    }
    return 20;
  }, [
    bulk,
    game.producerStrengthening.upgradeLevel,
    game.producers.length,
    unlockProducerNameBulk,
  ]);

  const requiredProducer = useMemo(() => {
    if (unlockProducerName) {
      return game.producers[game.producerStrengthening.upgradeLevel + 4];
    }
    return game.producers[game.producers.length - 1];
  }, [
    game.producerStrengthening.upgradeLevel,
    game.producers,
    unlockProducerName,
  ]);

  const requiredProducerBulk = useMemo(() => {
    if (unlockProducerNameBulk) {
      return game.producers[game.producerStrengthening.upgradeLevel + 4 + bulk];
    }
    return game.producers[game.producers.length - 1];
  }, [
    bulk,
    game.producerStrengthening.upgradeLevel,
    game.producers,
    unlockProducerNameBulk,
  ]);

  const toMultiplyProducer = useMemo(() => {
    const id = getPsToMultiplyId(
      game.producerStrengthening.upgradeLevel,
      game.producers.length
    );
    return game.producers[id];
  }, [game.producerStrengthening.upgradeLevel, game.producers]);

  const multipliedProducerName = useMemo(() => {
    return isMoleculeUnlocked("Ammonia", game)
      ? "all Producers"
      : toMultiplyProducer.name;
  }, [game, toMultiplyProducer.name]);

  const allProducersUnlocked = useMemo(() => {
    return isMoleculeUnlocked("Nitrogen", game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.molecule.graphNodes]);

  const disabled = useMemo(() => {
    return requiredProducer.value.lt(requiredUnits);
  }, [requiredProducer.value, requiredUnits]);

  const disabledBulk = useMemo(() => {
    return requiredProducerBulk.value.lt(requiredUnitsBulk);
  }, [requiredProducerBulk.value, requiredUnitsBulk]);

  const bulkUnlocked = useMemo(() => {
    return isMoleculeUnlocked("Sodium", game);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [game.molecule.graphNodes]);

  const maxBulk = useMemo(() => {
    if (bulkUnlocked) {
      return game.molecule.graphNodes["Sodium"] + 1;
    }
    return 0;
  }, [bulkUnlocked, game.molecule.graphNodes]);

  useEffect(() => {
    if (!disabledBulk && bulkUnlocked) {
      if (bulk < maxBulk) {
        setBulk((prev) => prev + 1);
      }
    }
  }, [
    bulk,
    bulkUnlocked,
    disabled,
    disabledBulk,
    game.producerStrengthening.upgradeLevel,
    game.producers.length,
    maxBulk,
  ]);

  const resetBulkText = useMemo(() => {
    if (bulkUnlocked && bulk > 1) {
      return `${bulk} times`;
    }
    return "";
  }, [bulk, bulkUnlocked]);

  const multiplier = useMemo(() => {
    if (bulkUnlocked && bulk > 1) {
      return game.producerStrengthening.multiplier.pow(bulk); //.toFixed(2);
    }
    return game.producerStrengthening.multiplier;
  }, [bulk, bulkUnlocked, game.producerStrengthening.multiplier]);
  return (
    <FormControl style={{ justifyContent: "center", display: "flex" }}>
      <FormLabel style={{ color: "white", fontSize: 12 }}>
        {bulkUnlocked && bulk > 0 ? (
          <div>
            Next Requirement: {requiredUnitsBulk} {requiredProducerBulk.name}
          </div>
        ) : (
          <div>
            Requires {requiredUnits} {requiredProducer.name}
          </div>
        )}
      </FormLabel>
      <Button
        disabled={disabled}
        style={{
          backgroundColor: disabled ? "transparent" : undefined,
          color: "white",
          borderColor: "lightblue",
          borderWidth: "1px",
          borderStyle: "solid",
          height: "80px",
          width: "260px",
        }}
        onClick={() => {
          //Dialog need to be adjusted
          if (bulkUnlocked && bulk > 0) {
            executeBulkProducerStrengthening({
              setGame,
              game,
              setProgressTimes: setTimeProgressArray,
              bulk,
            });
            setBulk(0);
          } else {
            if (!hidePsDialog) {
              props.setPsDialogOpen(true);
            } else {
              executeProducerStrengthening({
                setGame,
                game,
                setProgressTimes: setTimeProgressArray,
              });
            }
          }
        }}
      >
        <div style={{ textTransform: "none" }}>
          <div>
            Reset {resetBulkText} for x{prettifyNumber(multiplier)} Multiplier
            to {multipliedProducerName}
          </div>
          {!allProducersUnlocked && unlockProducerName && (
            <div>and unlock {unlockProducerName}s</div>
          )}
        </div>
      </Button>
      <FormLabel style={{ color: "white", fontSize: 12 }}>
        {bulkUnlocked && <div>Max Bulk: {maxBulk}</div>}
      </FormLabel>
    </FormControl>
  );
}

export function UnlockNextProducerButton({ onClick }: { onClick: () => void }) {
  const { game } = useContext(GameContext);
  const producer = game.producers[game.producerStrengthening.upgradeLevel + 4];
  const nextProducerName =
    game.producers[game.producerStrengthening.upgradeLevel + 5].name;
  const disabled = producer.value.lt(20);
  const multipliedProducerName = isMoleculeUnlocked("Ammonia", game)
    ? "all Producers"
    : game.producers[game.producerStrengthening.upgradeLevel + 1].name;

  const allProducersUnlocked = isMoleculeUnlocked("Nitrogen", game);
  const isBulkPossible = isMoleculeUnlocked("Sodium", game);

  return (
    <FormControl style={{ justifyContent: "center", display: "flex" }}>
      <FormLabel style={{ color: "white", fontSize: 12 }}>
        Requires 20 {producer.name}
      </FormLabel>
      <Button
        disabled={disabled}
        style={{
          backgroundColor: disabled ? "transparent" : undefined,
          color: "white",
          borderColor: "lightblue",
          borderWidth: "1px",
          borderStyle: "solid",
          height: "80px",
        }}
        onClick={() => onClick()}
      >
        <div style={{ textTransform: "none" }}>
          <div>
            Reset for x{prettifyNumber(game.producerStrengthening.multiplier)}{" "}
            Multiplier to {multipliedProducerName}
          </div>
          {!allProducersUnlocked && <div>and unlock {nextProducerName}s</div>}
        </div>
      </Button>
    </FormControl>
  );
}
export function ProducerStrengtheningButton({
  producer,
  lastProducer,
  requiredMultiplier,
  onClick,
  producerBoostMultiplier,
}: {
  producer: Producer;
  requiredMultiplier: number;
  lastProducer: Producer;
  producerBoostMultiplier: number;
  onClick: () => void;
}) {
  const disabled = lastProducer.value.lt(20 * requiredMultiplier);
  const { game } = useContext(GameContext);
  return (
    <FormControl style={{ justifyContent: "center", display: "flex" }}>
      <FormLabel style={{ color: "white", fontSize: 12 }}>
        Requires {20 * requiredMultiplier} {lastProducer.name}
      </FormLabel>
      <div>
        <Button
          disabled={disabled}
          style={{
            backgroundColor: disabled ? "transparent" : undefined,
            color: "white",
            borderColor: "lightblue",
            borderWidth: "1px",
            borderStyle: "solid",
            height: "80px",
          }}
          onClick={() => onClick()}
        >
          <div style={{ textTransform: "none" }}>
            <div>
              Reset for x{producerBoostMultiplier} Multiplier to{" "}
              {isMoleculeUnlocked("Ammonia", game)
                ? "all Producers"
                : producer.name}
            </div>
          </div>
        </Button>
      </div>
    </FormControl>
  );
}

export function BuySettingButton(props: {
  onClick: (buySetting: BuySetting) => void;
}) {
  const { buySetting } = useContext(GameContext);
  return (
    <Button
      variant="outlined"
      onClick={() => {
        const nextValue = getNextEnumValue(buySetting, BuySetting);
        props.onClick(nextValue ?? BuySetting.BuyOne);
      }}
      style={{
        backgroundColor: "transparent",
        color: "white",
        textTransform: "none",
      }}
    >
      {buySetting}
    </Button>
  );
}

export function BuyAllButton(props: {}) {
  const { game, setGame } = useContext(GameContext);
  return (
    <Tooltip title="Press 'b' to buy all until 10">
      <Button
        variant="outlined"
        style={{
          backgroundColor: "transparent",
          color: "white",
          textTransform: "none",
        }}
        onClick={() => {
          buyAllUntil10(game.producers, setGame);
          //buyAllUntil102(props.producers, props.producers[0].value);
        }}
      >
        Buy All Until 10
      </Button>
    </Tooltip>
  );
}

export function BuyAutomatorButton(props: { index: number; type: string }) {
  const { game, setGame } = useContext(GameContext);
  const producer = useMemo(() => {
    return game.producers[props.index];
  }, [game.producers, props.index]);

  const cost = useMemo(() => {
    let cost = new Decimal(100);
    if (producer.layerLevel >= 1) {
      const innerCost = innerGetCost(
        producer.baseCost,
        producer.costScaling,
        1
      );
      if (props.type === "Collector") {
        cost = innerCost.times(10 ** producer.layerLevel);
      }
      if (props.type === "Buyer") {
        cost = innerCost.times(1e8).times(1000 ** producer.layerLevel);
      }
    }
    return cost;
  }, [
    producer.baseCost,
    producer.costScaling,
    producer.layerLevel,
    props.type,
  ]);

  const bought = useMemo(() => {
    if (props.type === "Collector") {
      return producer.automateCollect;
    }
    if (props.type === "Buyer") {
      return producer.automateBuyBought;
    }
    return false;
  }, [producer.automateBuyBought, producer.automateCollect, props.type]);

  const canBuy = game.producers[0].value.greaterThanOrEqualTo(cost);

  const nameToLong = producer.name.length > 10;

  const activeDisabledText = useMemo(() => {
    if (props.type === "Collector") {
      return producer.automateCollectDisabled ? "Disabled" : "Active";
    }
    if (props.type === "Buyer") {
      return producer.automateBuy ? "Acitve" : "Disabled";
    }
    return "";
  }, [producer.automateBuy, producer.automateCollectDisabled, props.type]);
  return (
    <div style={{ paddingBottom: 16 }}>
      <Button
        variant="outlined"
        onClick={() => {
          if (bought) {
            setGame((prev) => ({
              ...prev,
              producers: prev.producers.map((producer, index) => {
                if (index === props.index) {
                  if (props.type === "Collector") {
                    return {
                      ...producer,
                      automateCollectDisabled:
                        !producer.automateCollectDisabled,
                    };
                  }
                  if (props.type === "Buyer") {
                    return {
                      ...producer,
                      automateBuy: !producer.automateBuy,
                    };
                  }
                }
                return producer;
              }),
            }));
          } else if (canBuy) {
            setGame((prev) => ({
              ...prev,
              producers: prev.producers.map((producer, index) => {
                if (index === 0) {
                  return {
                    ...producer,
                    value: producer.value.minus(cost),
                  };
                }
                if (index === props.index) {
                  if (props.type === "Collector") {
                    return {
                      ...producer,
                      automateCollect: true,
                    };
                  }
                  if (props.type === "Buyer") {
                    return {
                      ...producer,
                      automateBuy: true,
                      automateBuyBought: true,
                    };
                  }
                }
                return producer;
              }),
            }));
          }
        }}
        style={{
          backgroundColor: `rgba(33, 150, 243, ${
            bought ? 1 : canBuy ? 0.5 : 0
          })`,
          color: "white",
          textTransform: "none",
          width: "250px",
          minWidth: "250px",
        }}
      >
        <div>
          <div>{`${nameToLong ? producer.shortName : producer.name} Auto ${
            props.type
          }`}</div>
          <div>
            {bought ? (
              <div>{activeDisabledText}</div>
            ) : (
              <div>{`Cost: ${prettifyNumber(cost)} Particles`}</div>
            )}
          </div>
        </div>
      </Button>
    </div>
  );
}

export function SpeedBoostButton(props: { index: number }) {
  const { game, setGame } = useContext(GameContext);
  const { showSnackbar } = React.useContext(SnackbarContext);
  const [hovered, setHovered] = React.useState(false);
  const producer = useMemo(
    () => game.producers[props.index],
    [game.producers, props.index]
  );
  const particleValue = useMemo(
    () => game.producers[0].value,
    [game.producers]
  );
  const cost = useMemo(() => {
    return calculateSpeedShopCost(game, props.index);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [producer, game.molecule.graphNodes]);

  const canBuy = useMemo(
    () => particleValue.greaterThanOrEqualTo(cost),
    [particleValue, cost]
  );
  const fullyUpgraded = useMemo(
    () => producer.produceTime * game.neutronState.timeMultiplier <= 100,
    [game.neutronState.timeMultiplier, producer.produceTime]
  );
  let newProduceTime = useMemo(() => {
    return calculateNewProduceTime(producer.produceTime, speedFactor);
  }, [producer.produceTime]);
  const name = producer.name.length > 10 ? producer.shortName : producer.name;
  return (
    <div style={{ paddingBottom: 16 }}>
      <Button
        variant="outlined"
        style={{
          backgroundColor: fullyUpgraded
            ? "rgb(0,128,0)"
            : `rgba(128, 128, 0, ${canBuy ? 1 : 0.5})`,
          borderColor: "white",
          borderWidth: "2px",
          borderStyle: "solid",
          textTransform: "none",
          width: "18vw",
          minWidth: "230px",
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onClick={() => {
          if (particleValue.greaterThanOrEqualTo(cost) && !fullyUpgraded) {
            setGame((prev) => ({
              ...prev,
              producers: prev.producers.map((producer, index) => {
                if (index === 0) {
                  return {
                    ...producer,
                    value: producer.value.minus(cost),
                  };
                }
                if (index === props.index) {
                  return {
                    ...producer,
                    speedUpgradeLevel: producer.speedUpgradeLevel + 1,
                    produceTime: newProduceTime,
                  };
                }
                return producer;
              }),
            }));
            const unlocked = unlockAchievement(
              10,
              game.unlockedAchievementIds,
              setGame
            );
            if (unlocked) {
              showSnackbar(
                `Achievement Unlocked: ${getAchievementById(10)?.name}`
              );
            }
          }
        }}
      >
        <div>
          <div>{name}-Speed Boost</div>
          <div>{`Cost: ${prettifyNumber(cost)} Particles`}</div>
          <div>
            {fullyUpgraded ? (
              "Maxed"
            ) : (
              <div>
                {(
                  (producer.produceTime * game.neutronState.timeMultiplier) /
                  1000
                ).toFixed(2)}
                s{" "}
                {hovered
                  ? `--> ${(
                      (newProduceTime * game.neutronState.timeMultiplier) /
                      1000
                    ).toFixed(2)}s`
                  : ""}
              </div>
            )}
          </div>
        </div>
      </Button>
    </div>
  );
}

export function StrengtheningBoostButton(props: { onClick: () => void }) {
  const { game } = useContext(GameContext);
  const disabled =
    game.producerStrengthening.upgradeLevel <
    neededPsForSb + game.strengtheningBoost.upgradeLevel;
  return (
    <FormControl style={{ justifyContent: "center", display: "flex" }}>
      <FormLabel style={{ color: "white", fontSize: 12 }}>
        Requires {neededPsForSb + game.strengtheningBoost.upgradeLevel} Producer
        Strengthening
      </FormLabel>
      <Button
        disabled={disabled}
        style={{
          backgroundColor: disabled ? "transparent" : undefined,
          color: "white",
          borderColor: "lightblue",
          borderWidth: "1px",
          borderStyle: "solid",
          height: "80px",
        }}
        onClick={() => props.onClick()}
      >
        <div style={{ textTransform: "none" }}>
          <div style={{ fontSize: 12 }}>
            Reset Producer and Producer Strengthening
          </div>
          <div style={{ fontSize: 12 }}>
            for x
            {prettifyNumber(calculateNewProducerStrengthiningMultiplier(game))}{" "}
            total Multiplier to Producer Strengthening
          </div>
        </div>
      </Button>
    </FormControl>
  );
}

function getNextEnumValue<T>(
  currentValue: T,
  enumObject: Record<string, T>
): T | undefined {
  const enumValues = Object.values(enumObject) as Array<
    keyof typeof enumObject
  >;
  const enumKeys = Object.keys(enumObject) as Array<keyof typeof enumObject>;
  const currentIndex = enumValues.findIndex((value) => value === currentValue);
  const nextIndex = currentIndex + 1;
  if (nextIndex >= enumValues.length) return enumObject[enumKeys[0]];
  return enumObject[enumKeys[nextIndex]];
}
